interface MenuHighlightOptions {
  menuSelector: string;
  activeClass: string;
  linkAttribute?: string;
  exactMatch?: boolean;
}

class MenuHighlighter {
  private options: MenuHighlightOptions;

  constructor(options: MenuHighlightOptions) {
    this.options = {
      linkAttribute: options.linkAttribute || 'href',
      exactMatch: options.exactMatch || false,
      menuSelector: options.menuSelector,
      activeClass: options.activeClass
    };

    this.init();
  }

  private init(): void {
    const menu = document.querySelector(this.options.menuSelector!);

    if (!menu) {
      console.warn(`Menu element with selector "${this.options.menuSelector}" not found.`);
      return;
    }

    this.highlightActiveLinks(menu);
  }

  private highlightActiveLinks(menu: Element): void {
    const links = menu.querySelectorAll(`a[${this.options.linkAttribute}]`);
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname;

    links.forEach(link => {
      const href = link.getAttribute(this.options.linkAttribute!);

      if (href && this.isCurrentUrl(href, currentUrl, currentPath)) {
        link.classList.add(this.options.activeClass);
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove(this.options.activeClass);
        link.removeAttribute('aria-current');
      }
    });
  }

  private isCurrentUrl(href: string, currentUrl: string, currentPath: string): boolean {
    const absoluteHref = this.getAbsoluteUrl(href);
    const hrefPath = new URL(absoluteHref).pathname;

    if (this.options.exactMatch) {
      return absoluteHref === currentUrl;
    }

    // Special handling for home page
    if (hrefPath === '/' || absoluteHref.endsWith('/')) {
      // Only match home page exactly when not in exact mode
      return currentPath === '/' || currentPath === '';
    }

    // For other pages, check if current URL starts with the href
    // but also ensure we're not matching partial paths incorrectly
    return currentUrl.startsWith(absoluteHref) &&
      (currentPath === hrefPath || currentPath.startsWith(hrefPath + '/'));
  }

  private getAbsoluteUrl(url: string): string {
    // Handle hash links and empty URLs
    if (!url || url.startsWith('#')) {
      return window.location.href;
    }

    // Handle root-relative URLs
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }

    // Handle relative URLs (./ and ../)
    if (url.startsWith('./') || url.startsWith('../')) {
      const baseUrl = window.location.href.split('?')[0];
      const basePath = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
      return new URL(url, basePath).href;
    }

    // Handle absolute URLs
    try {
      return new URL(url).href;
    } catch {
      return url;
    }
  }

  public refresh(): void {
    const menu = document.querySelector(this.options.menuSelector as string);
    if (menu) {
      this.highlightActiveLinks(menu);
    }
  }
}

export { MenuHighlighter };
